import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAccount } from '../../hooks/useAccount'
import { useVideoUpload } from '../../hooks/useVideoUpload'
import { formatDuration, formatFileSize } from '../../utils/videoMetadata'

function VideoUploadTab() {
  const { t } = useTranslation('upload')
  const navigate = useNavigate()
  const { selectedAccount } = useAccount()
  const fileInputRef = useRef(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const {
    uploadQueue,
    uploadFiles,
    removeFromQueue,
    retryUpload,
    clearCompleted,
  } = useVideoUpload()

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)

      const files = Array.from(e.dataTransfer.files)
      if (selectedAccount && files.length > 0) {
        uploadFiles(files, selectedAccount.id)
      }
    },
    [selectedAccount, uploadFiles]
  )

  const handleFileSelect = useCallback(
    (e) => {
      const files = Array.from(e.target.files)
      if (selectedAccount && files.length > 0) {
        uploadFiles(files, selectedAccount.id)
      }
    },
    [selectedAccount, uploadFiles]
  )

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const completedCount = uploadQueue.filter((item) => item.status === 'complete').length
  const hasCompleted = completedCount > 0

  if (!selectedAccount) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 md:p-12 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noAccount.title')}</h3>
        <p className="text-gray-600">
          {t('noAccount.messageVideos')}
        </p>
      </div>
    )
  }

  return (
    <div>
      {/* Description */}
      <div className="mb-6">
        <p className="text-sm text-gray-600">
          {t('tabs.video.description')}
        </p>
      </div>

      {/* Drag and Drop Zone */}
      <div
        className={`relative rounded-lg border-2 border-dashed transition-all ${
          isDragOver
            ? 'border-green-500 bg-green-50'
            : 'border-gray-300 bg-white hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="p-6 sm:p-8 md:p-12 text-center">
          {/* Film Icon */}
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
            />
          </svg>
          <p className="mt-4 text-lg text-gray-900">{t('dropzone.dragVideo')}</p>
          <p className="mt-2 text-sm text-gray-600">{t('dropzone.or')}</p>
          <button
            type="button"
            onClick={handleBrowseClick}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            {t('dropzone.browseVideo')}
          </button>
          <p className="mt-4 text-xs text-gray-500">{t('dropzone.formatsVideo')}</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".mp4,.mov,video/mp4,video/quicktime"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('queue.title')}</h2>
            {hasCompleted && (
              <button onClick={clearCompleted} className="text-sm text-gray-600 hover:text-gray-900">
                {t('queue.clearCompleted', { count: completedCount })}
              </button>
            )}
          </div>

          <div className="space-y-3">
            {uploadQueue.map((item) => (
              <VideoUploadItem
                key={item.id}
                item={item}
                onRemove={() => removeFromQueue(item.id)}
                onRetry={() => retryUpload(item.id, selectedAccount.id)}
              />
            ))}
          </div>

          {hasCompleted && (
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/approval')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                {t('queue.viewApprovalQueue')}
                <svg className="ml-2 -mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function VideoUploadItem({ item, onRemove, onRetry }) {
  const { t } = useTranslation('upload')

  const statusText = {
    pending: t('status.waiting'),
    extracting: t('status.extractingMetadata'),
    uploading: t('status.uploading', { progress: item.progress }),
    complete: t('status.uploadComplete'),
    error: item.error || t('status.uploadFailed'),
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-start space-x-4">
        {/* Video Preview/Thumbnail */}
        <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
          {item.preview ? (
            <video
              src={item.preview}
              className="w-full h-full object-cover"
              muted
              playsInline
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg
                className="h-8 w-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{item.file.name}</p>
          <p className="text-sm text-gray-500">{formatFileSize(item.file.size)}</p>

          {/* Metadata (shown after extraction or completion) */}
          {item.metadata && (
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {formatDuration(item.metadata.duration)}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                  />
                </svg>
                {item.metadata.resolution}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                {item.metadata.codec}
              </span>
            </div>
          )}

          {/* Progress bar */}
          {(item.status === 'extracting' || item.status === 'uploading') && (
            <div className="mt-2">
              <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    item.status === 'extracting'
                      ? 'bg-blue-500 animate-pulse'
                      : 'bg-blue-500'
                  }`}
                  style={{
                    width: item.status === 'extracting' ? '50%' : `${item.progress}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Status */}
          <div className="mt-2 flex items-center space-x-2">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                item.status === 'complete'
                  ? 'bg-green-100 text-green-800'
                  : item.status === 'error'
                  ? 'bg-red-100 text-red-800'
                  : item.status === 'uploading'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {statusText[item.status]}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex flex-col space-y-2">
          {item.status === 'error' && (
            <button
              onClick={onRetry}
              className="text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              {t('actions.retry')}
            </button>
          )}
          {(item.status === 'pending' ||
            item.status === 'error' ||
            item.status === 'complete') && (
            <button onClick={onRemove} className="text-gray-400 hover:text-gray-600">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default VideoUploadTab
